// src/pages/Godown/GodownDashboard.jsx
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import orderService from '../../services/order.service';
import api from '../../services/api';
import './GodownManagerDashboard.css';

const GodownDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [godownIds, setGodownIds] = useState(() => {
    // 1. From auth context (set at login)
    if (Array.isArray(user?.godownIds) && user.godownIds.length > 0) return user.godownIds;
    // 2. Legacy fallback — only if junction array isn't present
    if (user?.godownId) return [user.godownId];
    return [];
  });

  const [stats, setStats]   = useState({ pending:0, confirmed:0, dispatched:0, delivered:0, total:0 });
  const [loading, setLoading] = useState(true);

  const primaryGodownId = useMemo(() => godownIds[0] || null, [godownIds]);

  // ── Step 1: resolve godownIds if not yet present ─────────────────────────
  useEffect(() => {
    if (godownIds.length > 0) return;
    const fetchProfile = async () => {
      try {
        const res = await api.get('/auth/me');
        const data = res.data?.data;
        if (Array.isArray(data?.godownIds) && data.godownIds.length > 0) {
          setGodownIds(data.godownIds);
        } else if (data?.godownId) {
          setGodownIds([data.godownId]);
        }
      } catch {
        // stays empty — will show "no godown assigned"
      }
    };
    fetchProfile();
  }, [godownIds.length]);

  // ── Step 2: fetch orders once we know the primary godown ─────────────────
  useEffect(() => {
    if (!primaryGodownId) {
      setLoading(false);
      return;
    }
    orderService.getOrdersByGodown(primaryGodownId)
      .then(res => {
        if (res.success && res.data) {
          const orders = res.data;
          setStats({
            pending:    orders.filter(o => o.orderStatus === 'PENDING_CONFIRMATION').length,
            confirmed:  orders.filter(o => o.orderStatus === 'CONFIRMED').length,
            dispatched: orders.filter(o => ['DISPATCHED','PARTIALLY_DISPATCHED'].includes(o.orderStatus)).length,
            delivered:  orders.filter(o => o.orderStatus === 'DELIVERED').length,
            total:      orders.length,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [primaryGodownId]);

  if (!primaryGodownId && !loading) return (
    <div className="godown-dashboard">
      <div style={{ padding:60, textAlign:'center', color:'#e53935' }}>
        <p style={{ fontSize:40 }}>⚠️</p>
        <p style={{ fontSize:16, fontWeight:600 }}>No godown assigned to your account.</p>
        <p style={{ color:'#6c757d' }}>Please contact your administrator to assign a godown.</p>
      </div>
    </div>
  );

  return (
    <div className="godown-dashboard">
      <div className="dashboard-header">
        <div>
          <h4 className="dashboard-title">My Godown</h4>
          <p style={{ fontSize:13, color:'#6c757d', margin:'2px 0 0 0' }}>
            Welcome, <strong>{user?.firstName}</strong>
            {primaryGodownId && (
              <> · Godown ID: <strong>{primaryGodownId}</strong></>
            )}
            {godownIds.length > 1 && (
              <> &middot; <span style={{ color:'#1565c0' }}>+{godownIds.length - 1} more</span></>
            )}
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="godown-summary-cards" style={{ marginBottom:32 }}>
        {[
          { label:'Total Orders', value:stats.total,      color:'#1565c0', bg:'#e3f2fd' },
          { label:'Pending',      value:stats.pending,    color:'#f57f17', bg:'#fff8e1' },
          { label:'Confirmed',    value:stats.confirmed,  color:'#2e7d32', bg:'#e8f5e9' },
          { label:'Dispatched',   value:stats.dispatched, color:'#6a1b9a', bg:'#f3e5f5' },
          { label:'Delivered',    value:stats.delivered,  color:'#1b5e20', bg:'#c8e6c9' },
        ].map(c => (
          <div key={c.label} className="godown-summary-card"
            style={{ background:c.bg, borderLeft:`4px solid ${c.color}` }}>
            <p className="gsc-count" style={{ color:c.color }}>{loading ? '—' : c.value}</p>
            <p className="gsc-label">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ display:'flex', flexDirection:'column', gap:12, maxWidth:500 }}>
        <p style={{ fontSize:14, fontWeight:700, color:'#1a1a1a', margin:0 }}>Quick Actions</p>
        {[
          { icon:'📦', title:'Manage Orders',    sub:'View, dispatch and update orders', path:'/orders/godown-dashboard' },
          { icon:'💰', title:'Payment Entries',  sub:'Add payments for delivered orders', path:'/orders/godown-dashboard' },
          { icon:'📋', title:'View Payments',    sub:'View full payment history',         path:'/orders/payments' },
        ].map(a => (
          <button key={a.title} onClick={() => navigate(a.path)}
            style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'16px 20px', background:'#fff', border:'1px solid #e9ecef',
              borderRadius:12, cursor:'pointer', textAlign:'left', width:'100%',
            }}
            onMouseOver={e => e.currentTarget.style.borderColor='#4caf50'}
            onMouseOut={e  => e.currentTarget.style.borderColor='#e9ecef'}
          >
            <div>
              <p style={{ margin:0, fontWeight:600, fontSize:14 }}>{a.icon} {a.title}</p>
              <p style={{ margin:'2px 0 0 0', fontSize:12, color:'#6c757d' }}>{a.sub}</p>
            </div>
            <span style={{ color:'#adb5bd', fontSize:18 }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default GodownDashboard;